import { useCallback, useEffect, useRef } from 'react'
import { useOverlayContext } from '../../shared/providers/overlayContextProvider/OverlayContext'

/**
 * Custom hook to handle the unsaved changes overlay.
 * It will trigger the save changes overlay if the form has unsaved changes.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Object} params.methods - Methods object from the useZionForm hook. This will be used to check if the form has unsaved changes with the dirty flag.
 * @param {Object} params.overlay - Overlay object to from the useOverlay hook.
 * @param {Function} [params.debug] - Optional debug function.
 * @param {string} [params.debugMessage='Unsaved changes overlay called: '] - Optional debug message.
 * @param {boolean} [params.forceWarning=false] - Optional flag to force the overlay to show. If no methods are provided, this will always show the overlay.
 * @param {boolean} [params.condition] - Optional condition to show the overlay. If passed, this would have precedence over the form state.
 * @param {string} [params.panelId] - Optional panelId to show the overlay only when the panel is on top.
 * @returns {Object} - Returns an object containing the subscriber function.
 */

const useUnsavedChangesOverlay = ({
  debug,
  debugMessage = 'Unsaved changes overlay called: ',
  methods,
  overlay,
  forceWarning = !methods,
  panelId,
  condition,
}) => {
  const { subscribe, unsubscribe } = useOverlayContext()
  const pendingRef = useRef(null)

  const subscriber = useCallback(
    // subscriber now accepts an optional pendingAction callback
    (topMostPanel, pendingAction) => {
      let shouldShowOverlay = condition ?? (methods?.formState?.isDirty || forceWarning)

      if (panelId) {
        shouldShowOverlay = topMostPanel === panelId && shouldShowOverlay
      }

      if (debug) debug(debugMessage, shouldShowOverlay)
      if (shouldShowOverlay) {
        // store the pending action to run if user confirms Exit
        pendingRef.current = typeof pendingAction === 'function' ? pendingAction : null
        overlay.open()
      } else {
        // clear pending if no overlay shown
        pendingRef.current = null
      }

      return !shouldShowOverlay
    },
    [methods?.formState?.isDirty, overlay, debug, debugMessage, forceWarning, panelId, condition]
  )

  // Subscribing just when the dirty state or condition changes
  useEffect(() => {
    subscribe(subscriber)

    // Attach helper to overlay so the panel can trigger the pending action
    if (overlay && pendingRef.current) {
      overlay.pendingAction = () => {
        const fn = pendingRef.current
        pendingRef.current = null
        if (typeof fn === 'function') fn()
      }
    }

    // Cleanup on unmount
    return () => {
      unsubscribe(subscriber)
      if (overlay?.pendingAction) {
        try {
          delete overlay.pendingAction
        } catch (e) {
          overlay.pendingAction = undefined
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriber])

  return { subscriber }
}

export default useUnsavedChangesOverlay
